import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FilterBar, type FilterOption } from '@/components/shared/FilterBar';

import {
  useUsageFiltersQuery,
  useUsageMonitoringFilters,
} from '../hooks/useUsageMonitoring';
import { UsageOrganizationSelector } from './UsageOrganizationSelector';

function optionLabel(
  item: { code?: string; name?: string; username?: string; first_name?: string; last_name?: string },
  fallback: string,
): string {
  if (item.code && item.name) return `${item.code} — ${item.name}`;
  if (item.name) return item.name;
  if (item.code) return item.code;
  const full = [item.first_name, item.last_name].filter(Boolean).join(' ').trim();
  if (full && item.username) return `${full} (${item.username})`;
  if (full) return full;
  if (item.username) return item.username;
  return fallback;
}

export function UsageFiltersBar({ showGranularity = true }: { showGranularity?: boolean }) {
  const { t } = useTranslation();
  const {
    filters,
    setDateFrom,
    setDateTo,
    setWarehouseId,
    setSupplierId,
    setUserId,
    setGranularity,
    applyWindowDefaults,
    queryParams,
  } = useUsageMonitoringFilters();

  const filtersQuery = useUsageFiltersQuery(queryParams);

  useEffect(() => {
    const window = filtersQuery.data?.window;
    if (!window) return;
    applyWindowDefaults(window.date_from, window.date_to);
  }, [filtersQuery.data?.window, applyWindowDefaults]);

  const filterOptions = useMemo<FilterOption[]>(() => {
    const data = filtersQuery.data;
    return [
      {
        id: 'warehouse',
        name: t('adoption.filters.warehouse'),
        options: (data?.warehouses ?? []).map((w) => ({
          value: w.id,
          label: optionLabel(w, String(w.id)),
        })),
      },
      {
        id: 'supplier',
        name: t('adoption.filters.supplier'),
        options: (data?.suppliers ?? []).map((s) => ({
          value: s.id,
          label: optionLabel(s, String(s.id)),
        })),
      },
      {
        id: 'user',
        name: t('adoption.filters.user'),
        options: (data?.users ?? []).map((u) => ({
          value: u.id,
          label: optionLabel(u, String(u.id)),
        })),
      },
    ];
  }, [filtersQuery.data, t]);

  const activeFilters = useMemo(() => {
    const chips: Array<{ id: string; label: string; value: string }> = [];
    const data = filtersQuery.data;
    if (filters.warehouseId != null) {
      const w = data?.warehouses.find((x) => x.id === filters.warehouseId);
      chips.push({
        id: 'warehouse',
        value: String(filters.warehouseId),
        label: w ? optionLabel(w, String(filters.warehouseId)) : String(filters.warehouseId),
      });
    }
    if (filters.supplierId != null) {
      const s = data?.suppliers.find((x) => x.id === filters.supplierId);
      chips.push({
        id: 'supplier',
        value: String(filters.supplierId),
        label: s ? optionLabel(s, String(filters.supplierId)) : String(filters.supplierId),
      });
    }
    if (filters.userId != null) {
      const u = data?.users.find((x) => x.id === filters.userId);
      chips.push({
        id: 'user',
        value: String(filters.userId),
        label: u ? optionLabel(u, String(filters.userId)) : String(filters.userId),
      });
    }
    return chips;
  }, [filters.warehouseId, filters.supplierId, filters.userId, filtersQuery.data]);

  const minDate =
    filtersQuery.data?.window?.module_min_date ||
    filtersQuery.data?.window?.default_date_from ||
    '2026-01-01';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <UsageOrganizationSelector />
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          {t('adoption.filters.dateFrom')}
          <input
            type="date"
            min={minDate}
            value={filters.dateFrom}
            onChange={(e) => {
              const next = e.target.value;
              setDateFrom(next && next < minDate ? minDate : next);
            }}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
          {t('adoption.filters.dateTo')}
          <input
            type="date"
            min={minDate}
            value={filters.dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
          />
        </label>
        {showGranularity ? (
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('adoption.filters.granularity')}
            <select
              value={filters.granularity}
              onChange={(e) => setGranularity(e.target.value === 'week' ? 'week' : 'month')}
              className="rounded-md border border-gray-300 px-2.5 py-1.5 text-sm text-gray-900 shadow-sm focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary"
            >
              <option value="month">{t('adoption.filters.granularityMonth')}</option>
              <option value="week">{t('adoption.filters.granularityWeek')}</option>
            </select>
          </label>
        ) : null}
        {filtersQuery.data?.window?.first_stockerai_usage ? (
          <p className="pb-1.5 text-xs text-gray-500">
            {t('adoption.filters.firstUse', {
              date: filtersQuery.data.window.first_stockerai_usage,
            })}
          </p>
        ) : null}
      </div>
      <FilterBar
        filters={filterOptions}
        activeFilters={activeFilters}
        onFilterChange={(next) => {
          if (Object.prototype.hasOwnProperty.call(next, 'warehouse')) {
            const wh = next.warehouse?.[0];
            setWarehouseId(wh ? Number(wh) : null);
          }
          if (Object.prototype.hasOwnProperty.call(next, 'supplier')) {
            const supl = next.supplier?.[0];
            setSupplierId(supl ? Number(supl) : null);
          }
          if (Object.prototype.hasOwnProperty.call(next, 'user')) {
            const usr = next.user?.[0];
            setUserId(usr ? Number(usr) : null);
          }
        }}
        onClearFilter={(id) => {
          if (id === 'warehouse') setWarehouseId(null);
          if (id === 'supplier') setSupplierId(null);
          if (id === 'user') setUserId(null);
        }}
        onClearAllFilters={() => {
          setWarehouseId(null);
          setSupplierId(null);
          setUserId(null);
        }}
      />
    </div>
  );
}
